import { Injectable } from '@angular/core';
import { ConfigurationService } from '../conf/configuration.service';
import { UserManagerService } from '../auth/user-manager.service';
import { Secciones, User } from '../user.model';

/**
 * Servicio para gestionar la visibilidad de módulos según las configuraciones
 * de GetAppConfig, ObtenerModulos y Usuarios
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleVisibilityService {

  /**
   * Mapeo de módulos a sus configuraciones AppConfigJson correspondientes
   */
  private readonly moduleConfigMap: { [key: string]: string } = {
    'venta': 'AppConfigJsonVenta',
    'auditorio': 'AppConfigJsonAuditorio',
    'tienda': 'AppConfigJsonTienda',
    'restauracion': 'AppConfigJsonRestauracion',
    'gestionar-pausadas': 'AppConfigJsonRestauracion', // Usa la misma que restauracion
    'localizador': 'AppConfigJsonLocalizador',
    'adminonline': null, // Solo revisa Usuarios
    'agenda': null, // Solo revisa Usuarios
    'gestionar-venta': 'AppConfigJsonVentaGestionar',
    'turno': 'AppConfigJsonLocalizador', // Según especificación usa AppConfigJsonLocalizador
    'abrircajon': 'AppConfigJsonCajon',
    'asociado': 'AppConfigJsonAsociado'
  };

  constructor(
    private configurationService: ConfigurationService,
    private userManagerService: UserManagerService
  ) {}

  /**
   * Verifica si un módulo debe ser visible según las reglas de la especificación
   * @param modulo - El módulo a verificar
   * @returns true si el módulo debe ser visible, false en caso contrario
   */
  isModuleVisible(modulo: Secciones): boolean {
    console.log('🔍 [MODULE VISIBILITY] Evaluando módulo:', {
      nombre: modulo.Nombre,
      ventana: modulo.Ventana,
      estado: modulo.Estado,
      codigoModulo: modulo.CodigoModulo
    });

    // 1. Verificar que el módulo tiene Estado = '1' (ya filtrado en getModulos, pero verificamos por seguridad)
    if (modulo.Estado !== '1') {
      console.log('❌ [MODULE VISIBILITY] Módulo rechazado: Estado !== "1"');
      return false;
    }

    const ventana = modulo.Ventana?.toLowerCase();
    if (!ventana) {
      console.log('❌ [MODULE VISIBILITY] Módulo rechazado: Sin ventana definida');
      return false;
    }

    const appConfigData = this.configurationService.getAppConfigValue();
    const appConfigRaw = this.configurationService.appConfig.value;
    const user = this.userManagerService.currentUserValue;

    console.log('🔍 [MODULE VISIBILITY] Datos disponibles:', {
      ventana: ventana,
      tieneAppConfigData: !!appConfigData,
      tieneAppConfigRaw: !!appConfigRaw,
      tieneDatosResult: !!(appConfigRaw && appConfigRaw.DatosResult),
      appConfigDataTipo: typeof appConfigData,
      appConfigRawTipo: typeof appConfigRaw,
      tieneUser: !!user,
      tienePermisos: !!(user && user.Permisos && user.Permisos.length > 0),
      appConfigDataKeys: appConfigData ? Object.keys(appConfigData) : [],
      appConfigRawKeys: appConfigRaw && appConfigRaw.DatosResult ? Object.keys(appConfigRaw.DatosResult) : []
    });

    // 2. Verificar si el módulo tiene configuración AppConfigJson
    const configJsonName = this.moduleConfigMap[ventana];
    console.log('🔍 [MODULE VISIBILITY] Configuración AppConfigJson:', {
      configJsonName: configJsonName,
      mapeoEncontrado: configJsonName !== undefined
    });

    // 3. Para módulos que solo revisan Usuarios (adminonline, agenda)
    if (configJsonName === null) {
      const tienePermiso = this.hasUserPermission(user, ventana);
      console.log('🔍 [MODULE VISIBILITY] Módulo solo revisa Usuarios:', {
        ventana: ventana,
        tienePermiso: tienePermiso,
        resultado: tienePermiso ? '✅ VISIBLE' : '❌ OCULTO'
      });
      return tienePermiso;
    }

    // 4. Si el módulo tiene configuración AppConfigJson, verificar Activo
    // getAppConfigValue() devuelve DatosResult directamente si existe
    let moduleConfig = null;
    
    // Prioridad 1: Intentar desde appConfigData (que es DatosResult directamente)
    if (configJsonName && appConfigData) {
      moduleConfig = appConfigData[configJsonName];
      console.log('🔍 [MODULE VISIBILITY] Buscando desde appConfigData (DatosResult):', {
        encontrado: !!moduleConfig,
        keysEnAppConfigData: appConfigData ? Object.keys(appConfigData) : []
      });
    }
    
    // Prioridad 2: Si no se encontró, intentar desde el objeto raw
    if (!moduleConfig && appConfigRaw && appConfigRaw.DatosResult) {
      moduleConfig = appConfigRaw.DatosResult[configJsonName];
      console.log('🔍 [MODULE VISIBILITY] Buscando desde appConfigRaw.DatosResult:', {
        encontrado: !!moduleConfig,
        keysEnDatosResult: appConfigRaw.DatosResult ? Object.keys(appConfigRaw.DatosResult) : []
      });
    }
    
    console.log('🔍 [MODULE VISIBILITY] Configuración del módulo encontrada:', {
      configJsonName: configJsonName,
      tieneModuleConfig: !!moduleConfig,
      activo: moduleConfig?.Activo,
      activoTipo: typeof moduleConfig?.Activo,
      activoEsNull: moduleConfig?.Activo === null,
      activoEsUndefined: moduleConfig?.Activo === undefined,
      moduleConfigKeys: moduleConfig ? Object.keys(moduleConfig) : [],
      appConfigDataEsDatosResult: appConfigData === (appConfigRaw?.DatosResult),
      debugInfo: {
        appConfigDataType: typeof appConfigData,
        appConfigRawType: typeof appConfigRaw,
        tieneAppConfigRawValue: !!appConfigRaw,
        tieneAppConfigRawDatosResult: !!(appConfigRaw && appConfigRaw.DatosResult)
      }
    });

    if (moduleConfig && moduleConfig.Activo !== undefined && moduleConfig.Activo !== null) {
        const activo = moduleConfig.Activo;
        const tienePermiso = this.hasUserPermission(user, ventana);
        
        // Si Activo = 1, el módulo es visible (siempre que tenga permisos)
        if (activo === '1' || activo === 1) {
          console.log('✅ [MODULE VISIBILITY] Activo = 1:', {
            ventana: ventana,
            tienePermiso: tienePermiso,
            resultado: tienePermiso ? '✅ VISIBLE' : '❌ OCULTO (sin permisos)'
          });
          return tienePermiso;
        }
        
        // Si Activo = 0 o null, revisar permisos del usuario
        if (activo === '0' || activo === 0 || activo === null) {
          console.log('🔍 [MODULE VISIBILITY] Activo = 0/null, revisando permisos:', {
            ventana: ventana,
            tienePermiso: tienePermiso,
            resultado: tienePermiso ? '✅ VISIBLE' : '❌ OCULTO (sin permisos)'
          });
          return tienePermiso;
        }
        
        // Si Activo tiene otro valor, no es visible
        console.log('❌ [MODULE VISIBILITY] Activo tiene valor no válido:', {
          ventana: ventana,
          activo: activo,
          resultado: '❌ OCULTO'
        });
        return false;
      }

    // 5. Si no se encuentra configuración o Activo es null/undefined,
    // revisar permisos del usuario (según especificación)
    const tienePermiso = this.hasUserPermission(user, ventana);
    console.log('🔍 [MODULE VISIBILITY] Sin configuración AppConfig, revisando permisos:', {
      ventana: ventana,
      tienePermiso: tienePermiso,
      resultado: tienePermiso ? '✅ VISIBLE' : '❌ OCULTO (sin permisos)'
    });
    return tienePermiso;
  }

  /**
   * Verifica si el usuario tiene permiso para ver el módulo
   * @param user - El usuario actual
   * @param ventana - El nombre de la ventana/módulo
   * @returns true si el usuario tiene permiso VER para el módulo
   */
  private hasUserPermission(user: User, ventana: string): boolean {
    if (!user || !user.Permisos || !ventana) {
      console.log('🔍 [USER PERMISSION] Sin usuario o permisos:', {
        tieneUser: !!user,
        tienePermisos: !!(user && user.Permisos),
        ventana: ventana
      });
      return false;
    }

    const ventanaLower = ventana.toLowerCase();
    const permisosFiltrados = user.Permisos.filter(
      (permiso) => permiso.Ventana?.toLowerCase() === ventanaLower
    );
    const tienePermisoVER = permisosFiltrados.some(
      (permiso) => permiso.NombreAccion === 'VER'
    );

    console.log('🔍 [USER PERMISSION] Verificando permisos:', {
      ventana: ventana,
      ventanaLower: ventanaLower,
      totalPermisos: user.Permisos.length,
      permisosFiltrados: permisosFiltrados.length,
      permisosFiltradosDetalle: permisosFiltrados.map(p => ({
        Ventana: p.Ventana,
        NombreAccion: p.NombreAccion,
        CodigoModulo: p.CodigoModulo
      })),
      tienePermisoVER: tienePermisoVER
    });

    return tienePermisoVER;
  }

  /**
   * Filtra una lista de módulos según su visibilidad
   * @param modulos - Lista de módulos a filtrar
   * @returns Lista de módulos visibles
   */
  filterVisibleModules(modulos: Secciones[]): Secciones[] {
    console.log('🔍 [FILTER MODULES] ===== INICIANDO FILTRADO DE MÓDULOS =====');
    console.log('🔍 [FILTER MODULES] Total módulos a evaluar:', modulos?.length || 0);

    if (!modulos || modulos.length === 0) {
      console.log('❌ [FILTER MODULES] No hay módulos para filtrar');
      return [];
    }

    const modulosVisibles = modulos.filter(modulo => this.isModuleVisible(modulo));

    console.log('✅ [FILTER MODULES] ===== RESULTADO DEL FILTRADO =====');
    console.log('✅ [FILTER MODULES] Módulos visibles:', modulosVisibles.length);
    console.log('✅ [FILTER MODULES] Módulos visibles detalle:', modulosVisibles.map(m => ({
      nombre: m.Nombre,
      ventana: m.Ventana,
      codigoModulo: m.CodigoModulo
    })));
    console.log('✅ [FILTER MODULES] Módulos ocultos:', modulos.length - modulosVisibles.length);
    console.log('✅ [FILTER MODULES] =========================================');

    return modulosVisibles;
  }
}

